/**
 * Sidebar v2 thread list drag adapters: the sorting strategy and collision
 * detection, memoized so drag-state re-renders keep their projection cache
 * and boundary hysteresis instead of rebuilding both closures per frame.
 * The collision detector additionally keys on the drag session and the
 * activation pointer position: its boundary-side and last-pointer-Y state
 * must start fresh for every lift instead of leaking from the previous one.
 */
import { useMemo } from "react";
import type { CollisionDetection } from "@dnd-kit/core";
import type { SortingStrategy } from "@dnd-kit/sortable";

import { createSidebarCollisionDetection } from "./sidebarV2Drag";
import { createSidebarSortingStrategy } from "./sidebarV2DragSort";
import { sidebarThreadKey, type SidebarV2Thread } from "./sidebarV2Threads";
import type { SidebarListItem } from "./sidebarV2List";

interface DragAdapterInput {
  readonly sidebarListItems: readonly SidebarListItem[];
  /** Changes on every drag start so the detector's hysteresis starts fresh. */
  readonly dragSessionKey: string | null;
  /** Pointer Y at pickup; seeds the detector's first boundary comparison. */
  readonly activationY: number | null;
  readonly settledThreads: readonly SidebarV2Thread[];
  readonly settledExpanded: boolean;
  readonly settledVisibleCount: number;
  readonly routeThreadKey: string | null;
  readonly snoozedThreadCount: number;
}

/** Build the list's drag adapters, recreating each only when its
 * behavior-affecting inputs change. */
export function useSidebarDragAdapters(input: DragAdapterInput): {
  readonly sortingStrategy: SortingStrategy;
  readonly collisionDetection: CollisionDetection;
} {
  const { sidebarListItems } = input;
  const settledOrder = useMemo(
    () => input.settledThreads.map((thread) => sidebarThreadKey(thread)),
    [input.settledThreads],
  );
  const sortingStrategy = useMemo(
    () =>
      createSidebarSortingStrategy({
        items: sidebarListItems,
        settledOrder,
        settledExpanded: input.settledExpanded,
        settledVisibleCount: input.settledVisibleCount,
        routeThreadKey: input.routeThreadKey,
        snoozedThreadCount: input.snoozedThreadCount,
      }),
    [
      sidebarListItems,
      settledOrder,
      input.settledExpanded,
      input.settledVisibleCount,
      input.routeThreadKey,
      input.snoozedThreadCount,
    ],
  );
  const collisionDetection = useMemo(
    () =>
      createSidebarCollisionDetection(() => true, {
        items: sidebarListItems,
        activationY: input.activationY,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dragSessionKey deliberately keys the memo though the closure reads only items/activationY: rebuilding it per drag session resets the boundary hysteresis.
    [sidebarListItems, input.dragSessionKey, input.activationY],
  );
  return { sortingStrategy, collisionDetection };
}
